import { ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { ACCENT_TILE, FOCUS_RING, SectionAccent, cx } from "./tokens";

export interface FilterSectionProps {
  id: string;
  /**
   * Step in the narrowing funnel. Numbers carry information only when each
   * step reduces the next; independent sections should leave this off (§23.4).
   */
  step?: number;
  icon: ReactNode;
  accent: SectionAccent;
  title: string;
  description?: string;
  /** Search input for this section — render it once the section exceeds 12 options. */
  search?: ReactNode;
  /** Overflow control, right-aligned in the header. */
  headerRight?: ReactNode;
  /** Collapsible header, used on mobile where sections become accordions (§23.14). */
  collapsible?: boolean;
  open?: boolean;
  onToggleOpen?: () => void;
  selectedCount?: number;
  children: ReactNode;
}

/**
 * One filter category. The section accent lives in the icon tile and nowhere
 * else — it must not travel into the options below it (§23.3).
 */
export default function FilterSection({
  id,
  step,
  icon,
  accent,
  title,
  description,
  search,
  headerRight,
  collapsible = false,
  open = true,
  onToggleOpen,
  selectedCount = 0,
  children,
}: FilterSectionProps) {
  const titleId = `${id}-title`;
  const expanded = collapsible ? open : true;

  const heading = (
    <div className="flex min-w-0 items-start gap-3">
      <span
        className={cx(
          "grid h-9 w-9 shrink-0 place-items-center rounded-[10px]",
          "[&>svg]:h-5 [&>svg]:w-5",
          ACCENT_TILE[accent],
        )}
        aria-hidden="true"
      >
        {icon}
      </span>

      <div className="min-w-0">
        <h3 id={titleId} className="flex items-center gap-2 text-[15px] font-semibold text-ink">
          {step !== undefined ? (
            <span className="tabular-nums text-ink-muted" aria-hidden="true">
              {step}
            </span>
          ) : null}
          <span className="truncate">{title}</span>
          {collapsible && selectedCount > 0 ? (
            <span className="rounded-full bg-select/[0.14] px-2 py-0.5 text-[11px] font-semibold text-select-ink">
              {selectedCount}
            </span>
          ) : null}
        </h3>
        {description ? (
          <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>
        ) : null}
      </div>
    </div>
  );

  return (
    <section
      id={id}
      aria-labelledby={titleId}
      className="scroll-mt-4 rounded-card border border-edge bg-surface p-4 shadow-panel sm:p-5"
    >
      <div className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        {collapsible ? (
          <button
            type="button"
            onClick={onToggleOpen}
            aria-expanded={expanded}
            aria-controls={`${id}-content`}
            className={cx("flex w-full items-start justify-between gap-3 rounded-chip text-left", FOCUS_RING)}
          >
            {heading}
            <ChevronDown
              className={cx(
                "mt-2 h-4 w-4 shrink-0 text-ink-muted transition-transform duration-150",
                expanded && "rotate-180",
              )}
              aria-hidden="true"
            />
          </button>
        ) : (
          heading
        )}

        {expanded && (search || headerRight) ? (
          <div className="flex min-w-0 shrink-0 flex-wrap items-center gap-3 lg:pt-0.5">
            {search ? <div className="w-full min-w-0 lg:w-80">{search}</div> : null}
            {headerRight}
          </div>
        ) : null}
      </div>

      {expanded ? (
        <div id={`${id}-content`} className="mt-4">
          {children}
        </div>
      ) : null}
    </section>
  );
}
